<script setup lang="ts">
import { Action } from '@/core/collections/general/action/action.interface';
import { Tab } from '@/core/collections/general/tab/tab.interface';
import Settings from '@/core/components/Settings.vue';
import { NxtIconButton, NxtPageHeader, NxtTabs } from '@nxtpeople/nxt-styles';
import { useKeycloak } from '@nxtpeople/nxt-vue-keycloak';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { Favorite } from '../collections/favorite/favorite.interface';
import { Team } from '../collections/teams/team.interface';

const route = useRoute();
const { state, getters, dispatch } = useStore();
const { t, locale } = useI18n();
const { decodedToken, keycloak } = useKeycloak();

const breadcrumbs = computed(() => state.headerStore?.breadcrumbs);

const actions = computed<Array<Action>>(() =>
   route.matched.reduce((actions, action_as_route): any => {
      return actions.concat((action_as_route.meta.actions as Array<Action>) || []);
   }, [] as Array<Action>)
);

const tabs = computed(() => (route.meta.tabs as Array<Tab>) || []);

const journey = computed(() => getters.journey);

// #region USERINFO */
const userInfo = computed(() => state.userInfo.userInfo);

function changeDefaultTeam(team: Partial<Team>) {
   if (!state.userInfo?.userInfo) return;
   const oldUserInfo = { ...state.userInfo?.userInfo };

   dispatch('userInfo/patchUser', { op: 'replace', path: 'default_team_id', value: team.id })
      .then(() => {
         window.location.reload();
      })
      .catch(() => {
         state.userInfo!.userInfo = oldUserInfo;
      });
}
// #endregion

// #region showSettingsPanel
const showSettingsPanel = ref(false);
function setShownPanel() {
   showSettingsPanel.value = true;
}
// #endregion

// #region FAVORITE PAGE
const tabName = computed(() => t(tabs.value.find((tab) => tab.route_name === route.name)?.title_key as string));
const locationHref = computed(() => window.location.origin + route.fullPath);

const favoritedPage = computed(() => userInfo.value?.favorites?.find((favorite: Favorite) => favorite.url === locationHref.value));

async function favoritePage() {
   if (!favoritedPage.value) {
      await dispatch('favorite/postFavorite', {
         name: `${journey.value.name[locale.value] ?? journey.value.name[journey.value.languages[0].code]} - ${tabName.value}`,
         url: window.location.href,
      });
   } else {
      deleteFavorite(favoritedPage.value.id);
   }
}

async function deleteFavorite(id: number) {
   await dispatch('favorite/deleteFavorite', id);
}
// #endregion

function changeUserInfoProfilePicture(url: string | null) {
   state.userInfo!.userInfo.profile_picture_url = url;
}
</script>

<template>
   <div class="page-header-content">
      <NxtPageHeader
         :user-info="userInfo"
         :user-actions="[
            userInfo ? { iconClass: 'fas fa-cog', label: $t('open_settings'), onClick: setShownPanel } : {},
            { iconClass: 'fa-solid fa-arrow-right-from-bracket', label: $t('logout'), to: keycloak.createLogoutUrl() },
         ]"
         :fallback-username="decodedToken?.preferred_username"
         :modules="userInfo?.modules || []"
         :manage-organisation-text="$t('manage_organisation')"
         :has-favorite-button="!!route.params.journeyId"
         :page-favorited="!!favoritedPage"
         @on-favorite="favoritePage"
         @change-default-team="changeDefaultTeam"
         @on-favorite-delete="deleteFavorite"
         @on-upload-picture="changeUserInfoProfilePicture"
      >
         <template #breadcrumbs>
            <RouterLink
               v-for="(breadcrumb, index) in breadcrumbs"
               :key="index"
               v-bind="breadcrumb"
               :title="breadcrumb.to"
               :class="{ active: index === (breadcrumbs?.length || 0) - 1 }"
            >
               {{ breadcrumb.content }}
            </RouterLink>
         </template>
         <template #pageActions>
            <div class="page-actions"></div>
            <RouterLink
               v-for="(action, index) in actions"
               :key="index"
               :to="{ name: action.route_name, params: action.params || undefined }"
               :class="{ active: route.name === action.route_name }"
            >
               <NxtIconButton
                  :name="action.icon"
                  :type="action.type || 'solid'"
                  :hover-text="action.hover_key ? $t(action.hover_key) : ''"
                  :click-has-stop-propagation="false"
                  :clickable="false"
               />
            </RouterLink>
         </template>
         <template
            v-if="userInfo"
            #globalActions
         >
            <RouterLink
               :to="{ name: 'bug-report' }"
               :class="{ active: route.name === 'bug-report' }"
            >
               <NxtIconButton
                  :name="'bug'"
                  :type="'solid'"
                  :hover-text="$t('report_a_bug')"
                  :click-has-stop-propagation="false"
                  :clickable="false"
               />
            </RouterLink>
            <a
               href="https://nxtpeople.notion.site/NxtForm-Menu-21a84503761f42e8844b9d95f92e87fe?pvs=74"
               target="_blank"
            >
               <NxtIconButton
                  :name="'circle-question'"
                  :type="'solid'"
                  :hover-text="$t('faq')"
                  :click-has-stop-propagation="false"
                  :clickable="false"
               />
            </a>
         </template>
      </NxtPageHeader>
      <NxtTabs>
         <RouterLink
            v-for="tab in tabs.filter((tab) => $auth(tab.scope, journey?.scopes))"
            :key="tab.route_name"
            :to="{ name: tab.route_name, params: tab.params || undefined }"
            :class="{ active: route.name === tab.route_name }"
         >
            {{ $t(tab.title_key) }}
         </RouterLink>
         <div class="page-filters"></div>
      </NxtTabs>
      <Settings
         :shown="showSettingsPanel"
         @close="showSettingsPanel = false"
      />
   </div>
</template>

<style scoped lang="scss">
.page-header-content {
   border-bottom: 1px solid var(--nxt-light-grey);
   &:deep(.nxt-breadcrumbs) {
      font-weight: bold;
   }

   &:deep(.nxt-page-header) {
      a {
         text-decoration: none;
         color: var(--nxt-dark);
      }
      .nxt-menu-card__card {
         z-index: var(--zindex-sticky);
      }
   }
}

a:hover {
   text-decoration: none;
}

.nxt-tabs {
   gap: var(--nxt-gutter-large);
   padding: 0;
   align-items: center;
}

.nxt-tabs a {
   padding: var(--nxt-gutter-large) var(--nxt-gutter-large);
}

.page-actions {
   display: flex;
   align-items: center;
   gap: var(--nxt-gutter-small);
}

.page-filters {
   display: flex;
   justify-content: flex-end;
   flex-grow: 1;
   padding-bottom: var(--nxt-gutter);
   margin-right: var(--nxt-gutter-large);
}

.page-filters:not(:has(*)) {
   display: none;
}
</style>
